import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Hero = () => {
  // Improved animation configuration
  const neonLineProps = {
    initial: { scaleX: 0 },
    animate: { scaleX: 1 },
    transition: { 
      duration: 1.5, 
      ease: [0.22, 1, 0.36, 1] // Custom easing for smooth animation
    }
  };
  
  const verticalNeonLineProps = {
    initial: { scaleY: 0 },
    animate: { scaleY: 1 },
    transition: { 
      duration: 1.5, 
      ease: [0.22, 1, 0.36, 1] 
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        delay: 0.5
      }
    }
  };

  return (
    <div className="relative min-h-screen flex items-center overflow-hidden bg-cyber-dark">
      {/* Background grid */}
      <div className="absolute inset-0 z-0 opacity-20">
        <div className="h-full w-full bg-[radial-gradient(#9D4EDD_1px,transparent_1px)] [background-size:40px_40px] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_70%,transparent_100%)]"></div>
      </div>
      
      {/* Neon lines with improved animations */}
      <motion.div 
        className="absolute top-0 left-0 right-0 h-1 bg-cyber-pink"
        {...neonLineProps}
      />
      
      <motion.div 
        className="absolute bottom-0 left-0 right-0 h-1 bg-cyber-blue"
        {...neonLineProps}
      />
      
      <motion.div 
        className="absolute top-0 bottom-0 left-0 w-1 bg-cyber-neon"
        {...verticalNeonLineProps}
      />
      
      <motion.div 
        className="absolute top-0 bottom-0 right-0 w-1 bg-cyber-purple"
        {...verticalNeonLineProps}
      />
      
      <div className="container mx-auto px-6 relative z-10 py-32">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left column - Text content with staggered animations */}
          <motion.div 
            className="lg:w-1/2 space-y-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={textVariants}>
              <span className="font-orbitron text-sm sm:text-base uppercase tracking-wider bg-cyber-pink/20 text-cyber-pink px-3 py-1 rounded-full border border-cyber-pink/30">
                Web Development Agency
              </span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-orbitron font-bold leading-tight"
              variants={textVariants}
            >
              <span className="text-white">Future-</span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-neon glitch-container">Driven</span>
              <br />
              <span className="text-white">Web Solutions</span>
            </motion.h1>
            
            <motion.p 
              className="text-gray-300 text-lg sm:text-xl max-w-2xl"
              variants={textVariants}
            >
              We create cutting-edge, responsive websites with stunning cyberpunk aesthetics and powerful functionality. From portfolio sites to e-commerce platforms, we build digital experiences that stand out.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 pt-6"
              variants={textVariants}
            >
              <Link to="/contact" className="cyber-button">
                <span>Start Your Project</span>
              </Link>
              
              <Link to="/portfolio" className="px-6 py-3 border border-cyber-neon text-cyber-neon hover:bg-cyber-neon/10 transition-all duration-300 font-medium tracking-wide font-rajdhani rounded-sm flex items-center justify-center">
                <span>View Our Work</span>
              </Link>
            </motion.div>
            
            <motion.div 
              className="pt-8 flex items-center gap-6"
              variants={textVariants}
            >
              <div className="flex -space-x-2">
                <div className="w-10 h-10 rounded-full border-2 border-cyber-dark bg-gradient-to-r from-pink-500 to-purple-600"></div>
                <div className="w-10 h-10 rounded-full border-2 border-cyber-dark bg-gradient-to-r from-purple-500 to-blue-600"></div>
                <div className="w-10 h-10 rounded-full border-2 border-cyber-dark bg-gradient-to-r from-blue-500 to-cyan-600"></div>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Trusted by <span className="text-white font-semibold">50+</span> businesses</p>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Right column - Image/3D object with spring animation */}
          <motion.div 
            className="lg:w-1/2 relative"
            variants={imageVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Simulated 3D element with divs and CSS */}
            <div className="w-full h-[400px] sm:h-[500px] relative">
              <div className="absolute inset-0 rounded-xl overflow-hidden border border-cyber-purple/30 glitch-container">
                <div className="absolute inset-0 bg-gradient-to-br from-cyber-dark via-cyber-gray to-cyber-blue/20"></div>
                
                {/* CPU-like circuit patterns */}
                <div className="absolute inset-0">
                  <div className="w-full h-full bg-[linear-gradient(90deg,transparent_9px,rgba(77,201,240,0.1)_10px),linear-gradient(0deg,transparent_9px,rgba(77,201,240,0.1)_10px)] bg-[length:10px_10px]"></div>
                </div>
                
                {/* Hologram cube with improved animation */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-60">
                  <motion.div 
                    className="relative w-full h-full"
                    animate={{ 
                      rotateY: 360,
                      rotateX: 45,
                      rotateZ: 45
                    }}
                    transition={{
                      duration: 20,
                      ease: "linear",
                      repeat: Infinity
                    }}
                  >
                    <div className="absolute top-0 left-0 w-full h-full border-4 border-cyber-neon/20 shadow-[0_0_10px_2px_rgba(77,201,240,0.3)] transform translate-z-12"></div>
                    <div className="absolute top-0 left-0 w-full h-full border-4 border-cyber-pink/20 shadow-[0_0_10px_2px_rgba(247,37,133,0.3)] transform rotate-45"></div>
                  </motion.div>
                </div>

                {/* Glitchy text overlay with smooth animation */}
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center"
                  animate={{ 
                    opacity: [0.1, 0.3, 0.1],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  <div className="text-cyber-neon font-orbitron text-7xl font-bold opacity-20 animate-neon-pulse">
                    WEB3
                  </div>
                </motion.div>

                {/* Horizontal scan line with smooth animation */}
                <motion.div 
                  className="absolute h-[2px] bg-cyber-pink opacity-70 left-0 right-0"
                  animate={{ top: [0, "100%"] }}
                  transition={{
                    duration: 3,
                    ease: "linear",
                    repeat: Infinity
                  }}
                ></motion.div>
              </div>
              
              {/* Neon edges with staggered animation */}
              <motion.div 
                className="absolute -top-2 -left-2 w-20 h-2 bg-cyber-pink"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 1 }}
              ></motion.div>
              <motion.div 
                className="absolute -top-2 -left-2 w-2 h-20 bg-cyber-pink"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
              ></motion.div>
              <motion.div 
                className="absolute -bottom-2 -right-2 w-20 h-2 bg-cyber-neon"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1, delay: 1.4 }}
              ></motion.div>
              <motion.div 
                className="absolute -bottom-2 -right-2 w-2 h-20 bg-cyber-neon"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 1, delay: 1.6 }}
              ></motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero; 